(function () {
  'use strict';

  const TIME_ZONE = 'Europe/Paris';
  const FAQ_URL = window.MDMTheme?.shippingUrl || window.MDMTheme?.faqUrl || '/pages/faq';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const holidayCache = new Map();

  function civilKey(day) {
    return `${day.year}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
  }

  function civilTimestamp(day) {
    return Date.UTC(day.year, day.month - 1, day.day, 12);
  }

  function civilFromTimestamp(timestamp) {
    const date = new Date(timestamp);
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
  }

  function toCivilDate(value = new Date(), timeZone = TIME_ZONE) {
    let result;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      result = { year, month, day };
    } else if (value && typeof value === 'object' && !(value instanceof Date)) {
      result = { year: value.year, month: value.month, day: value.day };
    } else {
      const instant = value instanceof Date ? value : new Date(value);
      if (!Number.isFinite(instant.getTime())) throw new RangeError('Date invalide.');
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(instant);
      const values = Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
      result = { year: values.year, month: values.month, day: values.day };
    }
    if (![result.year, result.month, result.day].every(Number.isInteger) || result.year < 1583 || result.year > 9999) throw new RangeError('Date civile invalide.');
    const normalized = civilFromTimestamp(civilTimestamp(result));
    if (civilKey(normalized) !== civilKey(result)) throw new RangeError('Date civile invalide.');
    return result;
  }

  function calendarDaysAfter(value, count) {
    return civilFromTimestamp(civilTimestamp(toCivilDate(value)) + count * DAY_MS);
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return { year, month, day };
  }

  function getFrenchPublicHolidays(year) {
    if (!Number.isInteger(year) || year < 1583 || year > 9999) throw new RangeError('Année invalide.');
    if (!holidayCache.has(year)) {
      const dates = ['01-01', '05-01', '05-08', '07-14', '08-15', '11-01', '11-11', '12-25'].map(date => `${year}-${date}`);
      const easter = easterSunday(year);
      [1, 39, 50].forEach(offset => dates.push(civilKey(calendarDaysAfter(easter, offset))));
      holidayCache.set(year, dates.sort());
    }
    return [...holidayCache.get(year)];
  }

  function isBusinessDay(value) {
    const day = toCivilDate(value);
    const weekday = new Date(civilTimestamp(day)).getUTCDay();
    return weekday !== 0 && weekday !== 6 && !getFrenchPublicHolidays(day.year).includes(civilKey(day));
  }

  function addBusinessDays(value, count) {
    if (!Number.isInteger(count) || count < 0 || count > 10000) throw new RangeError('Nombre de jours ouvrés invalide.');
    let result = toCivilDate(value);
    let remaining = count;
    while (remaining > 0) {
      result = calendarDaysAfter(result, 1);
      if (isBusinessDay(result)) remaining -= 1;
    }
    return result;
  }

  function estimateDelivery(value = new Date()) {
    const orderedOn = toCivilDate(value);
    const preparation = { min: addBusinessDays(orderedOn, 1), max: addBusinessDays(orderedOn, 2) };
    return {
      orderedOn,
      preparation,
      receipt: { min: addBusinessDays(preparation.min, 3), max: addBusinessDays(preparation.max, 6) },
      preparationBusinessDays: [1, 2],
      transitBusinessDays: [3, 6],
      carrier: 'Mondial Relay',
      destination: 'France métropolitaine',
      timeZone: TIME_ZONE
    };
  }

  function formatShortDate(value, includeYear = false) {
    const day = toCivilDate(value);
    return new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', day: 'numeric', month: 'long', ...(includeYear ? { year: 'numeric' } : {}) }).format(new Date(civilTimestamp(day)));
  }

  function formatDateRange(minimum, maximum) {
    const min = toCivilDate(minimum);
    const max = toCivilDate(maximum);
    if (civilTimestamp(max) < civilTimestamp(min)) throw new RangeError('Intervalle de dates invalide.');
    if (civilKey(min) === civilKey(max)) return formatShortDate(min);
    const crossesYear = min.year !== max.year;
    const start = min.year === max.year && min.month === max.month ? String(min.day) : formatShortDate(min, crossesYear);
    return `du ${start} au ${formatShortDate(max, crossesYear)}`;
  }

  const icons = {
    order: '<path d="M6 7h12l1 13H5L6 7Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>',
    preparation: '<path d="m12 3 8 4-8 4-8-4 8-4Z"/><path d="M4 7v10l8 4 8-4V7M12 11v10M8 5l8 4"/>',
    receipt: '<path d="M3 6h11v11H3zM14 10h4l3 4v3h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>'
  };

  function icon(name) {
    return `<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icons[name]}</svg>`;
  }

  function renderDelivery(element, now = new Date()) {
    if (!element) return;
    element.classList.add('delivery-estimate');
    element.setAttribute('role', 'region');
    element.setAttribute('aria-label', 'Estimation de livraison');
    element.setAttribute('aria-live', 'polite');
    const available = element.dataset.available !== 'false';
    element.classList.toggle('delivery-estimate--unavailable', !available);
    if (!available) {
      element.innerHTML = `<p class="delivery-unavailable">Les dates de livraison seront estimées lorsque ce produit sera de nouveau disponible.</p><a class="delivery-source" href="${FAQ_URL}" target="_blank" rel="noopener noreferrer">Consulter les informations de livraison</a>`;
      return;
    }
    const estimate = estimateDelivery(now);
    const preparation = formatDateRange(estimate.preparation.min, estimate.preparation.max);
    const receipt = formatDateRange(estimate.receipt.min, estimate.receipt.max);
    if (element.dataset.deliveryLayout === 'compact') {
      element.innerHTML = `<div class="delivery-compact-line">${icon('receipt')}<p>Commandez aujourd’hui<br><strong>Réception estimée ${receipt}</strong></p></div><details class="delivery-conditions"><summary>France · Mondial Relay · Voir les délais</summary><p>Préparation : ${preparation}. Transport : 3 à 6 jours ouvrés. Estimation hors week-ends et jours fériés nationaux.</p><a class="delivery-source" href="${FAQ_URL}">Toutes les informations de livraison</a></details>`;
      return;
    }
    element.innerHTML = `<p class="delivery-heading">Votre commande, étape par étape</p>
      <ol class="delivery-timeline">
        <li><span class="delivery-icon">${icon('order')}</span><span class="delivery-step-title">Commandez aujourd’hui</span><time datetime="${civilKey(estimate.orderedOn)}">${formatShortDate(estimate.orderedOn)}</time></li>
        <li><span class="delivery-icon">${icon('preparation')}</span><span class="delivery-step-title">Préparation</span><span class="delivery-date">${preparation}</span></li>
        <li><span class="delivery-icon">${icon('receipt')}</span><span class="delivery-step-title">Réception estimée</span><span class="delivery-date">${receipt}</span></li>
      </ol>
      <p class="delivery-location">Estimation en France métropolitaine avec Mondial Relay.</p>
      <p class="delivery-explanation">Préparation : 1 à 2 jours ouvrés. Transport : 3 à 6 jours ouvrés. Week-ends et jours fériés nationaux exclus.</p>
      <a class="delivery-source" href="${FAQ_URL}" target="_blank" rel="noopener noreferrer">Voir les délais annoncés</a>`;
  }

  function init(root, now = new Date()) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (typeof root.matches === 'function' && root.matches('[data-delivery-estimate]')) renderDelivery(root, now);
    root.querySelectorAll('[data-delivery-estimate]').forEach(element => renderDelivery(element, now));
  }

  const api = { toCivilDate, civilKey, getFrenchPublicHolidays, isBusinessDay, addBusinessDays, estimateDelivery, formatShortDate, formatDateRange, renderDelivery, init };
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    window.MirageDelivery = api;
    const refresh = () => init(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
    else refresh();
    document.addEventListener('mdm:product-ready', refresh);
    window.addEventListener('pageshow', refresh);
  }
})();
